import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ItemFilter from "../../planner/component/ItemFilter";
import SummaryByDate from "../component/SummaryByDate";
import { STORAGE_BUCKET } from "../../common/constants";
import {
  anyWordStartsWith,
  arrayContainsIgnoreCase,
  equalsIgnoreCase,
  getCustomerName,
  getStoreFilterStreets,
  orderMatchesStoreFilter,
} from "../../common/util";
import ItemsRollupView from "./ItemsRollupView";
import KitchenItemSwimLanes from "../../baker/screen/KitchenItemSwimLanes";
import { UserContext } from "../../context/UserContext";

export default function ItemsView(props) {
  const { user } = useContext(UserContext);
  const { data, fromDate, toDate, onItemProgressChange, onItemSelect, swimLanes, hideDateSummary } =
    props;
  const showSwimLanes = Boolean(swimLanes);

  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [date, setDate] = useState("");

  const [filteredResults, setFilteredResults] = useState([]);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  useEffect(() => {
    if (!hideDateSummary && fromDate) {
      setDate(moment(fromDate).format("YYYY-MM-DD"));
    }
  }, []);

  useEffect(() => {
    setFilteredResults([]);

    let arr = [];
    arr = data.filter((o) => {
      // check filters
      for (const [k, v] of Object.entries(filter)) {
        if (v.value) {
          if (k === "fulfillment" && !equalsIgnoreCase(o.fulfillment.type, v.value)) {
            return false;
          } else if (k === "store") {
            if (!orderMatchesStoreFilter(o, v.value, storeLocations)) {
              return false;
            }
          } else if (k === "frosting" && !equalsIgnoreCase(o.variant?.frosting?.name, v.value)) {
            return false;
          } else if (k === "flavor" && !equalsIgnoreCase(o.variant?.flavor?.name, v.value)) {
            return false;
          } else if (k === "filling" && !equalsIgnoreCase(o.variant?.filling?.name, v.value)) {
            return false;
          } else if (k === "tag" && !arrayContainsIgnoreCase(o.tags, v.value)) {
            return false;
          }
        }
      }
      // passed all filters
      return true;
    });
    const q = query.trim();
    if (q) {
      arr = arr.filter((o) => {
        const itemTitle = o.title || o.type || "";
        const customerName = getCustomerName(o.customer);
        return anyWordStartsWith(itemTitle, q) || anyWordStartsWith(customerName, q);
      });
    }
    setFilteredResults(arr);
  }, [filter, data, query, storeLocations]);

  if (!data || data.length === 0) {
    return (
      <Box px={2} pb={2} textAlign={"center"} sx={{ border: "1px solid #DDD" }}>
        <img style={{ width: "250px" }} src={`${STORAGE_BUCKET}/img/icon/search-results.svg`} />
        <Typography variant="subtitle1">Nothing found</Typography>
        <Typography variant="body1" color="textSecondary">
          Please change your search criteria & try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box my={2}>
        {hideDateSummary ? (
          <Paper elevation={0}>
            <Box
              py={0.75}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                marginRight: "5px",
              }}
            >
              <ItemFilter
                config={[
                  { name: "fulfillment", options: data.map((o) => o.fulfillment?.type) },
                  showStoreFilter ? { name: "store", options: storeStreets } : "",
                  { name: "frosting", options: data.map((o) => o.variant?.frosting?.name) },
                  { name: "flavor", options: data.map((o) => o.variant?.flavor?.name) },
                  { name: "filling", options: data.map((o) => o.variant?.filling?.name) },
                  { name: "tag", options: data.reduce((acc, val) => acc.concat(val.tags), []) },
                ]}
                filter={filter}
                onChange={(o) => setFilter({ ...filter, ...o })}
              />
              <TextField
                value={query}
                variant="outlined"
                size="small"
                onChange={(e) => setQuery(e.target.value)}
                placeholder="item / customer name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment sx={{ paddingRight: "10px" }}>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment>
                      {query && (
                        <IconButton onClick={() => setQuery("")} aria-label="Clear search">
                          <CloseOutlinedIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: "100%", sm: 220 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                    "& fieldset": {
                      borderColor: "primary.secondary",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        ) : (
          <ItemFilter
            config={[
              { name: "fulfillment", options: data.map((o) => o.fulfillment?.type) },
              showStoreFilter ? { name: "store", options: storeStreets } : "",
              { name: "frosting", options: data.map((o) => o.variant?.frosting?.name) },
              { name: "flavor", options: data.map((o) => o.variant?.flavor?.name) },
              { name: "filling", options: data.map((o) => o.variant?.filling?.name) },
              { name: "tag", options: data.reduce((acc, val) => acc.concat(val.tags), []) },
            ]}
            filter={filter}
            onChange={(o) => setFilter({ ...filter, ...o })}
          />
        )}
        {!hideDateSummary && (filteredResults.length > 0 || (fromDate && toDate)) && (
          <Box my={2} sx={{ backgroundColor: "#FFF", padding: "10px" }}>
            <SummaryByDate
              date={date}
              data={filteredResults.map((o) => {
                return {
                  date: o.fulfillment.date,
                  amount: o.total || o.price,
                  quantity: o.quantity,
                };
              })}
              from={moment(fromDate).format("YYYYMMDD")}
              to={moment(toDate).format("YYYYMMDD")}
              onChange={(value) => setDate(value !== date ? value : "")}
            />
          </Box>
        )}
      </Box>
      {filteredResults.length > 0 ? (
        showSwimLanes ? (
          <KitchenItemSwimLanes
            items={
              hideDateSummary
                ? filteredResults
                : filteredResults.filter((o) => (date ? date === o.fulfillment.date : true))
            }
            onItemProgressChange={onItemProgressChange}
            onItemSelect={onItemSelect}
          />
        ) : (
          <ItemsRollupView
            items={filteredResults.filter((o) =>
              date && date !== o.fulfillment.date ? false : true
            )}
          />
        )
      ) : (
        <Box my={2} px={2} pb={2} textAlign={"center"} sx={{ border: "1px solid #DDD" }}>
          <img style={{ width: "250px" }} src={`${STORAGE_BUCKET}/img/icon/search-results.svg`} />
          <Typography variant="subtitle1">Nothing found</Typography>
          <Typography variant="body1" color="textSecondary">
            Please change your search criteria & try again.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
